import { Vec3 } from '@/core/math/Vec3';
import { clamp01 } from '@/core/math/scalar';
import { simplex3 } from '@/core/math/noise';
import { CubeFace, faceToDirection } from '@/frames/PlanetaryCoordinates';
import { ChunkId, chunkBounds, chunkExtent } from './CubeSphere';
import {
  BiomeBands,
  TerrainParams,
  elevationAt,
  humidityAt,
  climateAt,
  classifyBiome,
  frequencyFor,
} from './TerrainPipeline';
import { SPECIES_INDEX } from './VegetationDef';
import { VegetationPopulation, vegetationCellArea, vegetationSampleAt } from './VegetationScatter';
import type { PaletteColors } from '@/universe/BodyDef';

/**
 * Builds the geometry for one terrain chunk.
 *
 * Runs inside a Web Worker (and, in tests, directly). Everything it needs is
 * either passed in or derived from the pure terrain pipeline, so it has no
 * dependency on Three.js, the DOM or any shared state.
 *
 * Two details are worth calling out:
 *
 *  - **Apron sampling.** The grid is generated one ring larger than the chunk in
 *    every direction. Those extra samples are used only to compute normals for
 *    the edge vertices. Because they land exactly on the neighbouring chunk's
 *    vertices, the normals match across the seam and there is no lighting crack.
 *
 *  - **Skirts.** A vertical apron is extruded downward from the chunk's border.
 *    It is invisible when neighbours are at the same level and hides the small
 *    gap when they differ by one level, without requiring index stitching or
 *    constraining the LOD tree to be balanced.
 */

export interface ChunkGeometryData {
  key: string;
  /** Chunk centre in planet-fixed coordinates, metres. Float64 precision. */
  centre: [number, number, number];
  /** Vertex positions relative to `centre`, metres. */
  positions: Float32Array;
  normals: Float32Array;
  /** Linear RGB albedo per vertex. */
  colors: Float32Array;
  /**
   * Per-vertex (slope 0..1, humidity 0..1).
   *
   * Two components, not three. A per-vertex biome index used to sit between
   * them: `TerrainMaterial` declared it, copied it into a varying, and never
   * read it — 4 bytes per vertex uploaded to the GPU every chunk to be
   * interpolated and thrown away. Biome does reach the shader, through the
   * vertex colour the mesher already blends it into.
   */
  surface: Float32Array;
  /**
   * Uint16 whenever the chunk's vertex count allows it, which is every
   * resolution this game ships: 64 vertices per edge is 4,481 vertices with
   * the skirt, far inside 65,536. Halves the single largest item in a chunk —
   * indices are a third of its whole payload, more than positions, normals or
   * colours. Uint32 remains the fallback so an experimental resolution cannot
   * silently produce wrapped indices and shredded geometry.
   */
  indices: Uint16Array | Uint32Array;
  /** Bounding sphere radius about `centre`, metres. */
  boundingRadius: number;
  /** Minimum and maximum surface radius within the chunk, metres. */
  minRadius: number;
  maxRadius: number;
  /** Vertices per edge, excluding the skirt. */
  resolution: number;
  level: number;
  /** Present only when the request carried `vegetation`. */
  vegetation?: ChunkVegetationData;
}

export interface ChunkBuildRequest {
  key: string;
  id: ChunkId;
  params: TerrainParams;
  /** Vertices per edge (a chunk is resolution x resolution vertices). */
  resolution: number;
  /** Palette used to colour the surface, from the body definition. */
  palette: PaletteColors;
  /** Biome classification bands, sent once per planet and cached in the worker. */
  biomeBands: [string, BiomeBands][];
  /** Biome base and detail colours, keyed by biome id. */
  biomeColors: [string, { color: [number, number, number]; detailColor: [number, number, number] }][];
  /**
   * Present only for chunks `PlanetSurface` has decided are fine enough (and
   * the body vegetated enough) to bother placing vegetation on. Absent
   * everywhere else, which is what keeps this system's cost proportional to
   * "chunks currently rendering at near-ground detail" rather than to the
   * whole planet.
   */
  vegetation?: ChunkVegetationRequest;
}

export interface ChunkVegetationRequest {
  /** Cells along a full cube-face edge — the planet's scatter grid. See `vegetationCellsPerFaceEdge`. */
  cellsPerFaceEdge: number;
  /**
   * Visit every `cellStride`-th cell in each axis, from
   * `vegetationCellStride(maxLevel - level)`. The *only* place the chunk's own
   * LOD level is allowed to influence placement, and it selects a nested
   * subset rather than a different set.
   */
  cellStride: number;
  /**
   * Multiplier on every biome's authored plants-per-km². The quality tier's
   * count knob; identical at every level, so turning it down thins the forest
   * everywhere at once without moving what is left.
   */
  densityScale: number;
  /**
   * Hard ceiling on instances emitted, a safety net against a pathological
   * biome rather than the density knob (cell size is the density knob). If it
   * ever bites, the chunk truncates in cell order, which is the one way a
   * coarse chunk could disagree with a fine one about a tree — size it so it
   * does not.
   */
  maxInstances: number;
  /** Fine chunks carry near-only grass/flowers; coarse billboard sources skip them entirely. */
  includeGroundCover?: boolean;
}

/**
 * Shared empty arrays handed to a `ChunkGeometryData` whose mesh buffers have
 * been released. Keeping the fields typed (rather than nullable) means the
 * dozens of places that read them stay untouched, and the one place that
 * genuinely cares — the renderer, before upload — is the only one that ever
 * sees them populated.
 */
const RELEASED_F32 = new Float32Array(0);
const RELEASED_INDICES = new Uint16Array(0);

/**
 * Drops a built chunk's vertex and index arrays.
 *
 * These are by far the largest thing the streamer holds: 312 KB per chunk at
 * `resolution: 64`, against a `maxChunks` budget of 1200 — measured at 222.8 MB
 * of live typed arrays with 731 chunks resident on Halcyon, and still climbing.
 * Nothing reads them after `TerrainRenderer.addChunk` has handed them to a
 * `THREE.BufferAttribute`; everything else on `ChunkGeometryData` that anyone
 * asks for later (`centre`, `boundingRadius`, `maxRadius`, `level`) is a
 * scalar.
 *
 * This alone frees nothing, because the `BufferAttribute` still holds the same
 * array. It is half of a pair: the renderer arranges for Three to release its
 * side once the data is actually on the GPU (`releaseAfterUpload` there). Both
 * references have to go.
 */
export function releaseChunkMeshArrays(data: ChunkGeometryData): void {
  data.positions = RELEASED_F32;
  data.normals = RELEASED_F32;
  data.colors = RELEASED_F32;
  data.surface = RELEASED_F32;
  data.indices = RELEASED_INDICES;
}

/** Struct-of-arrays instance data, mirroring the mesh arrays for cheap transfer. */
export interface ChunkVegetationData {
  count: number;
  /** Chunk-local positions, metres — same frame as `ChunkGeometryData.positions`. */
  positions: Float32Array;
  normals: Float32Array;
  /** Index into `VegetationDef.ALL_SPECIES` / `SPECIES_INDEX`. */
  speciesIndex: Uint16Array;
  /** Normalised 0..1 draw against the species' height/radius range. */
  scale: Float32Array;
  rotationY: Float32Array;
  colorJitter: Float32Array;
}

// The colour ramp is authored in BodyDef alongside the rest of a body's
// definition; re-exported here (type only, erased at build time) so the
// existing worker-side imports keep resolving from one declaration.
export type { PaletteColors } from '@/universe/BodyDef';

/**
 * Components in `ChunkGeometryData.surface`, and in the `aSurface` attribute
 * built from it. See that field for why it is 2 and not 3.
 */
export const SURFACE_COMPONENTS = 2;

const tmpDir = new Vec3();

/** Mixes toward `target` by `t` in place on a 3-tuple accumulator. */
function mix3(
  out: [number, number, number],
  a: readonly [number, number, number],
  b: readonly [number, number, number],
  t: number,
): void {
  out[0] = a[0] + (b[0] - a[0]) * t;
  out[1] = a[1] + (b[1] - a[1]) * t;
  out[2] = a[2] + (b[2] - a[2]) * t;
}

/**
 * Elevation-banded base colour from the planet palette, blended with the biome
 * colour. The palette provides the continental-scale look visible from orbit;
 * the biome provides the local look on the ground. Blending them means a planet
 * reads as itself at every distance.
 */
function surfaceColor(
  out: [number, number, number],
  palette: PaletteColors,
  biomeColor: readonly [number, number, number] | undefined,
  elevation: number,
  seaLevel: number,
  maxElevation: number,
  slope01: number,
  underwater: boolean,
  variation: number,
): void {
  if (underwater) {
    const depth = clamp01((seaLevel - elevation) / Math.max(1, maxElevation * 0.35));
    mix3(out, palette.shallowWater, palette.deepWater, depth);
    return;
  }

  const above = elevation - (seaLevel === Number.NEGATIVE_INFINITY ? -maxElevation * 0.5 : seaLevel);
  const f = clamp01(above / Math.max(1, maxElevation * 0.9));

  if (f < 0.03) mix3(out, palette.shore, palette.lowland, f / 0.03);
  else if (f < 0.28) mix3(out, palette.lowland, palette.midland, (f - 0.03) / 0.25);
  else if (f < 0.62) mix3(out, palette.midland, palette.highland, (f - 0.28) / 0.34);
  else mix3(out, palette.highland, palette.peak, (f - 0.62) / 0.38);

  if (biomeColor) {
    // Biome colour dominates near the ground, palette dominates from orbit; the
    // vertex colour is a fixed blend and the shader adds the distance-dependent
    // part, so no popping occurs.
    mix3(out, out, biomeColor, 0.55);
  }

  // Steep ground exposes bedrock regardless of biome.
  if (slope01 > 0.45) {
    const rock = palette.highland;
    mix3(out, out, rock, Math.min(0.85, (slope01 - 0.45) * 1.9));
  }

  // Variation is applied at low amplitude on purpose. It is indexed by
  // elevation, so neighbouring vertices get unrelated values — it is per-vertex
  // white noise, and white noise on a vertex grid cannot be filtered by
  // anything. At high amplitude it reads as a fixed speckle of dark dots at
  // ground level and as a visible dotted lattice from orbit, at every distance,
  // because the grid is exactly the frequency the renderer cannot resolve.
  // Surface texture belongs in the fragment shader, where it has a screen-space
  // footprint to fade against; this is only here to stop large flat areas
  // reading as a single painted colour.
  const v = 0.975 + variation * 0.05;
  out[0] *= v;
  out[1] *= v;
  out[2] *= v;
}

/**
 * Generates a chunk mesh.
 *
 * Vertex count is `(resolution + 1)^2` plus `4 * resolution` skirt vertices.
 */
export function buildChunk(request: ChunkBuildRequest): ChunkGeometryData {
  const { id, params, resolution } = request;
  const bounds = chunkBounds(id);
  const extent = chunkExtent(id.level);
  const step = extent / resolution;

  const biomeBands = new Map(request.biomeBands);
  const biomeColors = new Map(request.biomeColors);

  const n = resolution + 1;
  // Sample grid includes a one-vertex apron on every side for seamless normals.
  const gn = n + 2;
  const radii = new Float64Array(gn * gn);
  const elevations = new Float64Array(gn * gn);
  const dirs = new Float64Array(gn * gn * 3);

  const chunkArc = extent * params.radius;
  const sampleSpacing = chunkArc / resolution;

  for (let gy = 0; gy < gn; gy++) {
    const v = bounds.v0 + (gy - 1) * step;
    for (let gx = 0; gx < gn; gx++) {
      const u = bounds.u0 + (gx - 1) * step;
      faceToDirection(id.face as CubeFace, u, v, tmpDir);
      const idx = gy * gn + gx;
      dirs[idx * 3] = tmpDir.x;
      dirs[idx * 3 + 1] = tmpDir.y;
      dirs[idx * 3 + 2] = tmpDir.z;
      const e = elevationAt(tmpDir.x, tmpDir.y, tmpDir.z, params, sampleSpacing);
      elevations[idx] = e;
      // On worlds with oceans the rendered surface is the sea floor; the water
      // plane is drawn separately by the ocean renderer.
      radii[idx] = params.radius + e;
    }
  }

  // Chunk centre in planet-fixed coordinates, at the mean surface radius.
  const centreIdx = ((gn >> 1) * gn + (gn >> 1)) * 3;
  const centreDirX = dirs[centreIdx];
  const centreDirY = dirs[centreIdx + 1];
  const centreDirZ = dirs[centreIdx + 2];
  const centreRadius = radii[(gn >> 1) * gn + (gn >> 1)];
  const cx = centreDirX * centreRadius;
  const cy = centreDirY * centreRadius;
  const cz = centreDirZ * centreRadius;

  // Vegetation is scattered after the mesh, over the planet's own cell grid —
  // see `scatterVegetation` and `VegetationScatter.ts`. All the vertex loop
  // does for it is record each vertex's biome, which it classifies anyway.
  const vegRequest = request.vegetation;
  const vertexBiome: string[] | null = vegRequest ? new Array<string>(n * n) : null;

  const skirtCount = 4 * resolution;
  const vertexCount = n * n + skirtCount;
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const colors = new Float32Array(vertexCount * 3);
  const surface = new Float32Array(vertexCount * SURFACE_COMPONENTS);

  let minRadius = Infinity;
  let maxRadius = -Infinity;
  let boundingRadiusSq = 0;

  const colorOut: [number, number, number] = [0, 0, 0];
  const relief = params.profile.maxElevation || 1;

  // Smooth per-chunk colour variation, replacing what used to be a per-vertex
  // hash of the elevation's low-order bits (`(e*1000|0)%97`). That hash
  // changed chaotically between neighbouring vertices even where the terrain
  // itself was smooth, because a fraction-of-a-metre difference in elevation
  // can flip the low-order digits it extracts completely -- effectively
  // independent per-vertex white noise, which a vertex grid can't filter any
  // better than the fine noise bands the terrain shader already has to guard
  // with its own Nyquist fade (see `octavesFor`). Baked across a coarse,
  // orbital-LOD chunk, it read as a fine woven texture over the whole visible
  // ground -- the same failure mode as an aliased fbm band, just baked into
  // vertex colour at build time instead of sampled by the fragment shader.
  //
  // A single low-frequency simplex octave is smooth enough for any chunk that
  // can resolve it at all, and is skipped entirely (falls back to the neutral
  // 0.5, i.e. no variation) on a chunk too coarse to sample it without
  // aliasing -- the same guard `octavesFor` already applies to every other
  // terrain field.
  const VARIATION_WAVELENGTH_M = 40;
  const variationSafe = sampleSpacing <= VARIATION_WAVELENGTH_M * 0.5;
  const variationFreq = frequencyFor(params.radius, VARIATION_WAVELENGTH_M);
  const variationSeed = params.seed ^ 0x7c3a91d5;

  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const gi = (y + 1) * gn + (x + 1);
      const vi = y * n + x;

      const dx = dirs[gi * 3];
      const dy = dirs[gi * 3 + 1];
      const dz = dirs[gi * 3 + 2];
      const r = radii[gi];
      const e = elevations[gi];

      const px = dx * r - cx;
      const py = dy * r - cy;
      const pz = dz * r - cz;
      positions[vi * 3] = px;
      positions[vi * 3 + 1] = py;
      positions[vi * 3 + 2] = pz;

      const distSq = px * px + py * py + pz * pz;
      if (distSq > boundingRadiusSq) boundingRadiusSq = distSq;
      if (r < minRadius) minRadius = r;
      if (r > maxRadius) maxRadius = r;

      // Normal from the four neighbouring apron samples. Central differences on
      // the actual displaced surface, so slopes are exact rather than estimated
      // from the height field alone.
      const iL = gi - 1;
      const iR = gi + 1;
      const iD = gi - gn;
      const iU = gi + gn;

      const ax = dirs[iR * 3] * radii[iR] - dirs[iL * 3] * radii[iL];
      const ay = dirs[iR * 3 + 1] * radii[iR] - dirs[iL * 3 + 1] * radii[iL];
      const az = dirs[iR * 3 + 2] * radii[iR] - dirs[iL * 3 + 2] * radii[iL];
      const bx = dirs[iU * 3] * radii[iU] - dirs[iD * 3] * radii[iD];
      const by = dirs[iU * 3 + 1] * radii[iU] - dirs[iD * 3 + 1] * radii[iD];
      const bz = dirs[iU * 3 + 2] * radii[iU] - dirs[iD * 3 + 2] * radii[iD];

      let nx = ay * bz - az * by;
      let ny = az * bx - ax * bz;
      let nz = ax * by - ay * bx;
      const nl = Math.hypot(nx, ny, nz) || 1;
      nx /= nl;
      ny /= nl;
      nz /= nl;
      // Ensure the normal points away from the planet centre.
      if (nx * dx + ny * dy + nz * dz < 0) {
        nx = -nx;
        ny = -ny;
        nz = -nz;
      }
      normals[vi * 3] = nx;
      normals[vi * 3 + 1] = ny;
      normals[vi * 3 + 2] = nz;

      const cosSlope = Math.min(1, Math.max(0, nx * dx + ny * dy + nz * dz));
      const slope = Math.acos(cosSlope);
      const slope01 = clamp01(slope / (Math.PI / 2));

      const { temperature } = climateAt(dy, e, params);
      const humidity = humidityAt(dx, dy, dz, e, params);
      const underwater = params.seaLevel !== Number.NEGATIVE_INFINITY && e < params.seaLevel;
      const biomeId = classifyBiome(params, biomeBands, e, temperature, humidity, slope, underwater);
      if (vertexBiome) vertexBiome[vi] = biomeId;

      surface[vi * SURFACE_COMPONENTS] = slope01;
      surface[vi * SURFACE_COMPONENTS + 1] = humidity;

      const bc = biomeColors.get(biomeId);
      const variation = variationSafe
        ? simplex3(dx * variationFreq, dy * variationFreq, dz * variationFreq, variationSeed) * 0.5 + 0.5
        : 0.5;
      surfaceColor(
        colorOut,
        request.palette,
        bc?.color,
        e,
        params.seaLevel,
        relief,
        slope01,
        underwater,
        variation,
      );
      colors[vi * 3] = colorOut[0];
      colors[vi * 3 + 1] = colorOut[1];
      colors[vi * 3 + 2] = colorOut[2];
    }
  }

  // --- Skirt -------------------------------------------------------------
  // A skirt only has to cover the gap left when a neighbour is one LOD level
  // coarser, and that gap is bounded by how far the surface moves when the
  // sampling is halved — which is the chunk's own relief, not its width.
  //
  // Scaling it by chunk width instead looks fine on the ground and is a
  // disaster from orbit: a coarse chunk three hundred kilometres across grows a
  // ten-kilometre wall on every edge, and those walls read as a hard lattice
  // drawn over the whole planet. The cap keeps a skirt proportionate to the
  // terrain it is hiding at every level.
  // The gap to cover is set by how far the surface moves when the sampling is
  // halved, which the chunk's own relief bounds — but only loosely, because a
  // neighbour may be more than one level coarser and a smooth chunk can still
  // sit beside a jagged one. Sizing it purely by this chunk's relief leaves
  // visible gaps; sizing it by chunk width alone grows kilometre-high walls
  // from orbit. Take the larger of a generous multiple of the local relief and
  // a small fraction of the chunk, and cap it so it can never become a cliff.
  // The `chunkArc * 0.012` floor this used to carry was the source of the long
  // terrain-coloured slivers across the near field. An LOD gap is a HEIGHT
  // error — how far the surface moves when the sampling is halved — and height
  // error is bounded by the terrain's relief, never by how wide the chunk is.
  // Tying the floor to the arc made the two scale together, so an unsubdivided
  // root chunk on Halcyon (arc ~9,600 km) grew a 115 km wall on every edge, and
  // those walls project as long slivers wherever a coarse chunk is still in
  // view. Coastal ground showed it worst precisely because its low relief let
  // the arc term win outright.
  //
  // Bounded by the body's own relief budget instead: no skirt can ever need to
  // be deeper than the full range the height field can produce.
  //
  // Margin widened from the original 2.5x/+12/floor 2 (still capped the same
  // way, so this cannot reopen the orbit-cliff bug the cap exists for).
  // 'chunkRelief' is THIS chunk's own sampled relief, and a coarse chunk's own
  // sampling can alias past a sharp feature a finer neighbour resolves — the
  // gap the skirt has to hide is then bigger than this chunk's own relief
  // estimate says, and the shortfall reads as a hairline crack at the LOD
  // seam that aliases in and out as the camera moves, closer to "flickering"
  // than to an obviously-missing wall. A more generous multiple buys margin
  // against that underestimate; it does not fix its cause (this chunk still
  // has no idea what its neighbour actually looks like — see NEXT_SESSION.md
  // §2 for the real fix, a balanced quadtree that stitches instead of
  // skirting).
  const chunkRelief = Number.isFinite(maxRadius - minRadius) ? maxRadius - minRadius : relief;
  // A skirt is only a crack guard. The previous relief-wide cap let coarse
  // chunks extrude kilometre-high vertical walls, visible as striped "plate
  // boundaries" at oblique angles. Keep a little local margin, but never let
  // the workaround become visible terrain in its own right.
  const bodyRelativeCeiling = Math.min(240, relief * 0.035 + 12);
  const skirtDepth = Math.max(3, Math.min(chunkRelief * 1.5 + 8, bodyRelativeCeiling));
  let skirtVertex = n * n;
  const skirtIndexMap = new Int32Array(skirtCount);
  let skirtSlot = 0;

  const addSkirt = (edgeVertexIndex: number): void => {
    const px = positions[edgeVertexIndex * 3];
    const py = positions[edgeVertexIndex * 3 + 1];
    const pz = positions[edgeVertexIndex * 3 + 2];
    // Push down along the planet radial direction.
    const wx = px + cx;
    const wy = py + cy;
    const wz = pz + cz;
    const wl = Math.hypot(wx, wy, wz) || 1;
    positions[skirtVertex * 3] = px - (wx / wl) * skirtDepth;
    positions[skirtVertex * 3 + 1] = py - (wy / wl) * skirtDepth;
    positions[skirtVertex * 3 + 2] = pz - (wz / wl) * skirtDepth;
    normals[skirtVertex * 3] = normals[edgeVertexIndex * 3];
    normals[skirtVertex * 3 + 1] = normals[edgeVertexIndex * 3 + 1];
    normals[skirtVertex * 3 + 2] = normals[edgeVertexIndex * 3 + 2];
    colors[skirtVertex * 3] = colors[edgeVertexIndex * 3];
    colors[skirtVertex * 3 + 1] = colors[edgeVertexIndex * 3 + 1];
    colors[skirtVertex * 3 + 2] = colors[edgeVertexIndex * 3 + 2];
    surface[skirtVertex * SURFACE_COMPONENTS] = surface[edgeVertexIndex * SURFACE_COMPONENTS];
    surface[skirtVertex * SURFACE_COMPONENTS + 1] = surface[edgeVertexIndex * SURFACE_COMPONENTS + 1];
    skirtIndexMap[skirtSlot++] = skirtVertex;
    skirtVertex++;
  };

  // Bottom, right, top, left edges (excluding the final corner of each so the
  // ring has exactly 4 * resolution entries).
  const edgeVertices: number[] = [];
  for (let x = 0; x < resolution; x++) edgeVertices.push(x);
  for (let y = 0; y < resolution; y++) edgeVertices.push(y * n + (n - 1));
  for (let x = resolution; x > 0; x--) edgeVertices.push((n - 1) * n + x);
  for (let y = resolution; y > 0; y--) edgeVertices.push(y * n);
  for (const v of edgeVertices) addSkirt(v);

  // --- Indices -----------------------------------------------------------
  // Winding is counter-clockwise seen from *outside* the planet, which is what
  // WebGL calls front-facing. For every cube face `faceToDirection` is built so
  // that d/du x d/dv points outward, so a triangle listed (u, then u+1, then
  // v+1) faces the sky. Reversing those two makes every chunk cap back-facing:
  // the ground under the camera vanishes and what remains on screen is the
  // inside of the far hemisphere plus the skirts, which reads as a black
  // surface with a bright grid on it.
  //
  // Every quad split along the SAME diagonal (b-c below) reads, on displaced
  // terrain, as a field of regular polygon-shaped facets aligned with that one
  // diagonal direction: the two triangles either side of a shared b-c edge get
  // meaningfully different interpolated normals wherever the height field has
  // real curvature within one quad, which a mesh sampled right up to its own
  // Nyquist limit (see `octavesFor`) always has somewhere. A single fixed
  // diagonal turns that into a coherent crease repeated identically over every
  // quad in the mesh, which is exactly what a viewer sees as a tiled hexagon
  // pattern baked into the ground at any distance or LOD — reported as
  // matching the wireframe once the wireframe was actually looked at. Same
  // family of bug as the axis-aligned Worley lattice and the two-tap cloud
  // dither elsewhere in this codebase: a single direction, repeated
  // everywhere, reads as an artificial grid. Alternating the split direction
  // in a checkerboard, the standard fix for this in heightmap terrain meshing,
  // breaks that shared direction into two that cancel each other out visually
  // instead of one that repeats.
  const quadCount = resolution * resolution;
  const indices =
    vertexCount <= 65536
      ? new Uint16Array(quadCount * 6 + skirtCount * 6)
      : new Uint32Array(quadCount * 6 + skirtCount * 6);
  let t = 0;
  for (let y = 0; y < resolution; y++) {
    for (let x = 0; x < resolution; x++) {
      const a = y * n + x;
      const b = a + 1;
      const c = a + n;
      const d = c + 1;
      if ((x + y) % 2 === 0) {
        // Split along b-c, same as before.
        indices[t++] = a;
        indices[t++] = b;
        indices[t++] = c;
        indices[t++] = b;
        indices[t++] = d;
        indices[t++] = c;
      } else {
        // Split along a-d instead. (a, b, d) and (a, d, c) keep the same
        // outward winding sense as (a, b, c)/(b, d, c) -- each still lists a
        // "+u" step before a "+v" step from its own starting corner.
        indices[t++] = a;
        indices[t++] = b;
        indices[t++] = d;
        indices[t++] = a;
        indices[t++] = d;
        indices[t++] = c;
      }
    }
  }
  // The skirt ring already walks the border in the direction that puts its
  // outward face away from the chunk, so it keeps its winding.
  for (let i = 0; i < skirtCount; i++) {
    const next = (i + 1) % skirtCount;
    const top0 = edgeVertices[i];
    const top1 = edgeVertices[next];
    const bot0 = skirtIndexMap[i];
    const bot1 = skirtIndexMap[next];
    indices[t++] = top0;
    indices[t++] = bot0;
    indices[t++] = top1;
    indices[t++] = top1;
    indices[t++] = bot0;
    indices[t++] = bot1;
  }

  const vegetation =
    vegRequest && vertexBiome
      ? scatterVegetation(vegRequest, {
          id,
          params,
          n,
          gn,
          step,
          u0: bounds.u0,
          v0: bounds.v0,
          radii,
          elevations,
          normals,
          surface,
          vertexBiome,
          cx,
          cy,
          cz,
        })
      : undefined;

  return {
    key: request.key,
    centre: [cx, cy, cz],
    positions,
    normals,
    colors,
    surface,
    indices,
    boundingRadius: Math.sqrt(boundingRadiusSq) + skirtDepth,
    minRadius,
    maxRadius,
    resolution,
    level: id.level,
    vegetation,
  };
}

/** Everything the scatter pass reads back out of the finished mesh. */
interface ScatterContext {
  id: ChunkId;
  params: TerrainParams;
  /** Vertices per edge (`resolution + 1`). */
  n: number;
  /** Sample-grid edge including the one-vertex apron (`n + 2`). */
  gn: number;
  /** Face-uv distance between adjacent samples. */
  step: number;
  u0: number;
  v0: number;
  radii: Float64Array;
  elevations: Float64Array;
  normals: Float32Array;
  surface: Float32Array;
  vertexBiome: string[];
  cx: number;
  cy: number;
  cz: number;
}

const scatterDir = new Vec3();

/**
 * Places this chunk's vegetation on the planet's fixed scatter grid.
 *
 * Deliberately a second pass over its own grid rather than a few lines inside
 * the vertex loop, because the two grids are not the same grid and must not be
 * allowed to become the same grid again — that coupling is exactly what made
 * every LOD level grow a different forest (see `VegetationScatter.ts`). The
 * cell grid is a power-of-two subdivision of the cube face, so this chunk owns
 * a whole `cellsPerChunkEdge` square block of it with no partial cells at the
 * borders.
 *
 * What the mesh *is* used for is the ground underneath a cell: elevation comes
 * from bilinear interpolation of the same `radii` samples the vertices were
 * built from, so a plant sits on the surface actually drawn rather than on a
 * separately re-evaluated height field that would disagree with it by a few
 * centimetres of float noise. Biome, slope and normal come from the nearest
 * vertex — all three are already computed there, and at the finest level that
 * vertex is closer than half a cell away.
 *
 * Instances accumulate in a plain array and are packed once at the end. The
 * grid bounds the count on its own (at most one plant per population per
 * cell), so there is no need to guess a capacity up front the way the old
 * fixed `maxInstances` allocation did.
 */
function scatterVegetation(request: ChunkVegetationRequest, ctx: ScatterContext): ChunkVegetationData {
  const { id, params, n, gn, step, u0, v0, radii, elevations, normals, surface, vertexBiome } = ctx;
  const { cellsPerFaceEdge, densityScale, maxInstances } = request;

  const cellArea = vegetationCellArea(params.radius, cellsPerFaceEdge);
  const cellStep = 2 / cellsPerFaceEdge;
  // Whole cells per chunk edge. The cell grid is finer than any chunk in
  // practice; the clamp only guards a body small enough (or a cell size large
  // enough) to invert that.
  const cellsPerChunkEdge = Math.max(1, cellsPerFaceEdge >> id.level);
  // Both are powers of two, so this divides exactly; a chunk coarser than the
  // stride would otherwise visit its first cell only.
  const stride = Math.max(1, Math.min(request.cellStride, cellsPerChunkEdge));
  const visitsPerEdge = cellsPerChunkEdge / stride;
  const firstCellU = id.ix * cellsPerChunkEdge;
  const firstCellV = id.iy * cellsPerChunkEdge;

  const populations: VegetationPopulation[] =
    request.includeGroundCover === false ? ['structure'] : ['groundCover', 'structure'];

  const outPosition: number[] = [];
  const outNormal: number[] = [];
  const outSpecies: number[] = [];
  const outScale: number[] = [];
  const outRotation: number[] = [];
  const outJitter: number[] = [];
  let count = 0;

  for (let cv = 0; cv < visitsPerEdge && count < maxInstances; cv++) {
    const cellV = firstCellV + cv * stride;
    const v = (cellV + 0.5) * cellStep - 1;
    for (let cu = 0; cu < visitsPerEdge && count < maxInstances; cu++) {
      const cellU = firstCellU + cu * stride;
      const u = (cellU + 0.5) * cellStep - 1;

      // Fractional position in the sample grid. Sample `gx` sits at
      // `u0 + (gx - 1) * step`, and a cell centre is always strictly inside
      // the chunk, so `gx` lands in (1, n) and both interpolation neighbours
      // exist without clamping.
      const gxf = (u - u0) / step + 1;
      const gyf = (v - v0) / step + 1;
      const gx0 = Math.floor(gxf);
      const gy0 = Math.floor(gyf);
      const fx = gxf - gx0;
      const fy = gyf - gy0;
      const i00 = gy0 * gn + gx0;
      const i10 = i00 + 1;
      const i01 = i00 + gn;
      const i11 = i01 + 1;

      const elevation =
        (elevations[i00] * (1 - fx) + elevations[i10] * fx) * (1 - fy) +
        (elevations[i01] * (1 - fx) + elevations[i11] * fx) * fy;
      const radius =
        (radii[i00] * (1 - fx) + radii[i10] * fx) * (1 - fy) +
        (radii[i01] * (1 - fx) + radii[i11] * fx) * fy;

      // Nearest mesh vertex, for the fields the vertex loop already resolved.
      const vx = Math.min(n - 1, Math.max(0, Math.round(gxf) - 1));
      const vy = Math.min(n - 1, Math.max(0, Math.round(gyf) - 1));
      const vi = vy * n + vx;

      const biomeId = vertexBiome[vi];
      if (!biomeId) continue;
      const underwater = params.seaLevel !== Number.NEGATIVE_INFINITY && elevation < params.seaLevel;
      const depth = underwater ? params.seaLevel - elevation : 0;
      // `surface` stores slope normalised against a quarter turn.
      const slope = surface[vi * SURFACE_COMPONENTS] * (Math.PI / 2);

      for (const population of populations) {
        if (count >= maxInstances) break;
        const sample = vegetationSampleAt(
          cellU,
          cellV,
          id.face,
          biomeId,
          underwater,
          depth,
          slope,
          cellArea,
          densityScale,
          params,
          population,
        );
        if (!sample) continue;

        faceToDirection(id.face as CubeFace, u, v, scatterDir);
        outPosition.push(
          scatterDir.x * radius - ctx.cx,
          scatterDir.y * radius - ctx.cy,
          scatterDir.z * radius - ctx.cz,
        );
        outNormal.push(normals[vi * 3], normals[vi * 3 + 1], normals[vi * 3 + 2]);
        outSpecies.push(SPECIES_INDEX.get(sample.speciesId) ?? 0);
        outScale.push(sample.scale);
        outRotation.push(sample.rotationY);
        outJitter.push(sample.colorJitter);
        count++;
      }
    }
  }

  return {
    count,
    positions: Float32Array.from(outPosition),
    normals: Float32Array.from(outNormal),
    speciesIndex: Uint16Array.from(outSpecies),
    scale: Float32Array.from(outScale),
    rotationY: Float32Array.from(outRotation),
    colorJitter: Float32Array.from(outJitter),
  };
}
